/**
 * Create and edit a property.
 *
 * One component for both, because the fields are identical and two components drift.
 * `useForm` from Refine handles the create-vs-edit distinction; the only difference
 * visible here is the heading and where it navigates on success.
 *
 * Create only gets a sidebar (map preview + a photo queue): there is no
 * propertyId to attach a real upload to until the property exists, so photos
 * picked here sit in memory and are uploaded — then one is marked primary —
 * right after `onFinish` returns the new id. Edit mode stays the single-column
 * form it always was; ongoing photo curation, including changing the primary,
 * lives on PropertyShow.tsx where the rest of a property's files already do.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useForm } from '@refinedev/core';
import { PROPERTY_TYPES } from 'shared';
import { api } from '../../../lib/api.js';
import { uploadDocument } from '../../../lib/dataProvider.js';
import { AddressMapPreview } from '../../../components/AddressMapPreview.js';
import { PropertyPhotoQueue, type QueuedPhoto } from '../../../components/PropertyPhotoQueue.js';
import {
  EMPTY_OWNER_ASSIGNMENT,
  PropertyOwnerPicker,
  type OwnerAssignment,
} from '../../../components/PropertyOwnerPicker.js';
import {
  Field,
  FormErrors,
  Select,
  TextArea,
  TextInput,
  fieldErrors,
} from '../../../components/Form.js';

interface PropertyFormState {
  name: string;
  propertyType: string;
  line1: string;
  line2: string;
  city: string;
  postalCode: string;
  county: string;
  yearBuilt: string;
  parcelNumber: string;
  notes: string;
}

const EMPTY: PropertyFormState = {
  name: '',
  propertyType: 'SINGLE_FAMILY',
  line1: '',
  line2: '',
  city: '',
  postalCode: '',
  county: '',
  yearBuilt: '',
  parcelNumber: '',
  notes: '',
};

interface PropertyRecord {
  id: string;
  name: string;
  propertyType: string;
  address: {
    line1: string;
    line2: string | null;
    city: string;
    postalCode: string;
    county: string | null;
  };
  yearBuilt: number | null;
  parcelNumber: string | null;
  notes: string | null;
}

export function PropertyForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState<PropertyFormState>(EMPTY);
  const [photoQueue, setPhotoQueue] = useState<QueuedPhoto[]>([]);
  const [primaryPhotoId, setPrimaryPhotoId] = useState<string | null>(null);
  const [ownerAssignment, setOwnerAssignment] = useState<OwnerAssignment>(EMPTY_OWNER_ASSIGNMENT);

  // Mirrors photoQueue for the unmount cleanup below, so object URLs created
  // for a queued photo are released even if the form is abandoned without
  // submitting — the effect's own closure would otherwise only ever see the
  // empty array it started with.
  const photoQueueRef = useRef<QueuedPhoto[]>([]);
  useEffect(() => {
    photoQueueRef.current = photoQueue;
  }, [photoQueue]);
  useEffect(
    () => () => {
      for (const photo of photoQueueRef.current) URL.revokeObjectURL(photo.previewUrl);
    },
    [],
  );

  const { onFinish, query, mutation } = useForm<PropertyRecord>({
    resource: 'properties',
    action: isEdit ? 'edit' : 'create',
    ...(isEdit ? { id } : {}),
    redirect: false,
  });

  const record = query?.data?.data;

  // Populated once the record arrives. Keyed on the id so that navigating between
  // two edit screens re-seeds the fields instead of keeping the first record's
  // values — a stale form that silently PATCHes the wrong values is worse than a
  // flash of empty inputs.
  useEffect(() => {
    if (!record) return;
    setForm({
      name: record.name,
      propertyType: record.propertyType,
      line1: record.address.line1,
      line2: record.address.line2 ?? '',
      city: record.address.city,
      postalCode: record.address.postalCode,
      county: record.address.county ?? '',
      yearBuilt: record.yearBuilt === null ? '' : String(record.yearBuilt),
      parcelNumber: record.parcelNumber ?? '',
      notes: record.notes ?? '',
    });
  }, [record?.id, record]);

  const set =
    <K extends keyof PropertyFormState>(key: K) =>
    (value: PropertyFormState[K]) =>
      setForm((previous) => ({ ...previous, [key]: value }));

  const errors = fieldErrors(mutation.error);

  /**
   * Upload the queued photos to the just-created property, then mark the
   * chosen one primary. Runs after the property already exists — a photo
   * that fails to upload, or a primary that fails to set, does not undo the
   * property itself. Failures are summarized in one alert and the count
   * still reads as success on the property page, which now really does
   * exist and can be fixed up from there — the same partial-failure shape
   * the lease wizard uses for its own post-creation steps.
   */
  async function uploadQueuedPhotos(propertyId: string) {
    if (photoQueue.length === 0) return;

    const results = await Promise.allSettled(
      photoQueue.map(async (photo) => {
        const uploaded = await uploadDocument(photo.file, {
          propertyId,
          documentType: 'PROPERTY_PHOTO',
        });
        return { queueId: photo.id, documentId: uploaded.id };
      }),
    );

    let failures = results.filter((result) => result.status === 'rejected').length;
    const succeeded = results
      .filter(
        (result): result is PromiseFulfilledResult<{ queueId: string; documentId: string }> =>
          result.status === 'fulfilled',
      )
      .map((result) => result.value);

    const primaryUpload = succeeded.find((upload) => upload.queueId === primaryPhotoId);
    if (primaryUpload) {
      try {
        await api(`/documents/${primaryUpload.documentId}/primary`, { method: 'PATCH' });
      } catch {
        failures += 1;
      }
    }

    if (failures > 0) {
      window.alert(
        `The property was created, but ${String(failures)} photo${failures === 1 ? '' : 's'} ` +
          "didn't finish uploading or couldn't be set as primary. Add or fix them from the " +
          "property's page.",
      );
    }
  }

  /**
   * Grant the chosen owner's interest in the just-created property — the same
   * `POST /ownerships` OwnershipForm.tsx uses for "Add owner", just fired here
   * with the propertyId that only exists after `onFinish` returns. Left blank
   * (the default) this is a no-op; a failure here doesn't undo the property.
   */
  async function assignOwner(propertyId: string) {
    if (!ownerAssignment.ownerId) return;

    try {
      // Percentage → basis points, same rounding OwnershipForm.tsx does: "33.333"
      // typed into a percent box has to land on an integer somewhere.
      const basisPoints = Math.round(Number(ownerAssignment.percent) * 100);
      await api('/ownerships', {
        method: 'POST',
        body: JSON.stringify({
          propertyId,
          ownerId: ownerAssignment.ownerId,
          basisPoints,
          startDate: ownerAssignment.startDate,
        }),
      });
    } catch (caught) {
      window.alert(
        "The property was created, but the owner couldn't be assigned automatically — " +
          `${caught instanceof Error ? caught.message : 'an error occurred'}. Add it from the ` +
          "property's page.",
      );
    }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();

    // The API takes a nested address object; the form is flat because a flat form is
    // easier to render. Assembling it here rather than nesting the state keeps the
    // input handlers trivial.
    const payload = {
      name: form.name,
      propertyType: form.propertyType,
      address: {
        line1: form.line1,
        line2: form.line2 || null,
        city: form.city,
        // state is not a field. Michigan-only is a compliance decision, not a
        // default someone can change in a dropdown (invariant 7) — the zod schema
        // pins it and a CHECK constraint enforces it.
        postalCode: form.postalCode,
        county: form.county || null,
      },
      // Empty string means "not provided", which for a nullable number is null, not
      // 0. `Number('')` is 0, so this cannot be a bare cast.
      yearBuilt: form.yearBuilt === '' ? null : Number(form.yearBuilt),
      parcelNumber: form.parcelNumber || null,
      notes: form.notes || null,
    };

    const saved = (await onFinish(payload)) as { data?: PropertyRecord } | undefined;
    const savedId = saved?.data?.id ?? id;

    if (savedId && !isEdit) {
      await uploadQueuedPhotos(savedId);
      await assignOwner(savedId);
    }
    if (savedId) void navigate(`/staff/properties/${savedId}`);
  }

  if (isEdit && query?.isLoading) return <p className="muted">Loading…</p>;

  const formCard = (
    <form className="stack card" onSubmit={(event) => void submit(event)}>
      <FormErrors error={mutation.error} />

      <Field label="Name" name="name" required error={errors.name}>
        <TextInput name="name" value={form.name} onChange={set('name')} required />
      </Field>

      <Field label="Type" name="propertyType" required error={errors.propertyType}>
        <Select
          name="propertyType"
          value={form.propertyType}
          onChange={set('propertyType')}
          options={PROPERTY_TYPES}
        />
      </Field>

      <Field label="Street address" name="address.line1" required error={errors['address.line1']}>
        <TextInput name="address.line1" value={form.line1} onChange={set('line1')} required />
      </Field>

      <Field label="Unit / suite" name="address.line2" error={errors['address.line2']}>
        <TextInput name="address.line2" value={form.line2} onChange={set('line2')} />
      </Field>

      <Field label="City" name="address.city" required error={errors['address.city']}>
        <TextInput name="address.city" value={form.city} onChange={set('city')} required />
      </Field>

      <Field
        label="ZIP"
        name="address.postalCode"
        required
        hint="Michigan only — the state is fixed at MI."
        error={errors['address.postalCode']}
      >
        <TextInput
          name="address.postalCode"
          value={form.postalCode}
          onChange={set('postalCode')}
          required
        />
      </Field>

      <Field
        label="County"
        name="address.county"
        hint="Wayne, Oakland, Macomb…"
        error={errors['address.county']}
      >
        <TextInput name="address.county" value={form.county} onChange={set('county')} />
      </Field>

      <Field label="Year built" name="yearBuilt" error={errors.yearBuilt}>
        <TextInput
          name="yearBuilt"
          type="number"
          min="1800"
          max="2100"
          value={form.yearBuilt}
          onChange={set('yearBuilt')}
        />
      </Field>

      <Field label="Parcel number" name="parcelNumber" error={errors.parcelNumber}>
        <TextInput name="parcelNumber" value={form.parcelNumber} onChange={set('parcelNumber')} />
      </Field>

      <Field label="Notes" name="notes" error={errors.notes}>
        <TextArea name="notes" value={form.notes} onChange={set('notes')} />
      </Field>

      <div className="row">
        <button type="submit" className="button" disabled={mutation.isLoading}>
          {mutation.isLoading ? 'Saving…' : isEdit ? 'Save changes' : 'Create property'}
        </button>
        <button
          type="button"
          className="button button--quiet"
          onClick={() =>
            void navigate(isEdit && id ? `/staff/properties/${id}` : '/staff/properties')
          }
        >
          Cancel
        </button>
      </div>
    </form>
  );

  return (
    <section>
      <h1>{isEdit ? `Edit ${record?.name ?? 'property'}` : 'Add property'}</h1>

      {isEdit ? (
        formCard
      ) : (
        <div className="person-layout">
          {formCard}
          <div className="stack">
            <AddressMapPreview line1={form.line1} city={form.city} postalCode={form.postalCode} />
            <PropertyPhotoQueue
              queue={photoQueue}
              primaryId={primaryPhotoId}
              onChange={(queue, primaryId) => {
                setPhotoQueue(queue);
                setPrimaryPhotoId(primaryId);
              }}
            />
            <PropertyOwnerPicker value={ownerAssignment} onChange={setOwnerAssignment} />
          </div>
        </div>
      )}
    </section>
  );
}
